import { useEffect, useState, type CSSProperties } from "react";

export interface TourStep {
  /** CSS selector for the element this step spotlights. Steps whose target
   *  isn't in the DOM (e.g. a section that only renders after some state)
   *  are skipped automatically rather than breaking the tour. */
  selector: string;
  title: string;
  body: string;
}

const PAD = 6;

/**
 * Minimal, dependency-free guided tour: dims the page, cuts a spotlight over
 * one element at a time, and shows a step card with Back/Next/Skip. No
 * external library — kept consistent with the capture-pwa implementation.
 */
export function Tour({
  steps,
  active,
  onClose,
}: {
  steps: TourStep[];
  active: boolean;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (active) setIndex(0);
  }, [active]);

  useEffect(() => {
    if (!active) return;

    function measure(i: number) {
      if (i < 0) { onClose(); return; }
      if (i >= steps.length) { onClose(); return; }
      const el = document.querySelector(steps[i].selector) as HTMLElement | null;
      if (!el) { measure(i + 1); return; }
      if (i !== index) { setIndex(i); return; }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setRect(el.getBoundingClientRect());
    }
    measure(index);

    function refresh() {
      const el = document.querySelector(steps[index]?.selector ?? "") as HTMLElement | null;
      if (el) setRect(el.getBoundingClientRect());
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, steps.length - 1));
      else if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    }
    window.addEventListener("resize", refresh);
    window.addEventListener("scroll", refresh, true);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", refresh);
      window.removeEventListener("scroll", refresh, true);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index, steps.length]);

  if (!active || !rect) return null;

  const step = steps[index];
  const last = index + 1 >= steps.length;

  return (
    <div className="tour-scrim" onClick={onClose}>
      <div
        className="tour-spotlight"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        }}
      />
      <div className="tour-card" style={cardPosition(rect)} onClick={(e) => e.stopPropagation()}>
        <p className="tour-step-count">Step {index + 1} of {steps.length}</p>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        <div className="tour-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Skip</button>
          <span style={{ flex: 1 }} />
          {index > 0 && (
            <button type="button" className="btn-secondary" onClick={() => setIndex(index - 1)}>
              Back
            </button>
          )}
          <button type="button" className="btn-primary" onClick={() => (last ? onClose() : setIndex(index + 1))}>
            {last ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

function cardPosition(rect: DOMRect): CSSProperties {
  const cardWidth = 300;
  let left = rect.left;
  if (left + cardWidth > window.innerWidth - 16) left = window.innerWidth - cardWidth - 16;
  if (left < 16) left = 16;

  const spaceBelow = window.innerHeight - rect.bottom;
  if (spaceBelow > 220) return { top: rect.bottom + 14, left };
  return { top: Math.max(16, rect.top - 14), left, transform: "translateY(-100%)" };
}
